/**
 * Thin React Query wrapper around the resolveCreatorByUsername fetcher.
 *
 * Responsibility: bind the fetcher to the cache key and forward `enabled`
 * from the caller. Navigation / relanding decisions are NOT made here —
 * the next task (T12) handles those in a separate hook that gates on this
 * one's result.
 *
 * Conventions mirrored from sibling hooks (useCreatorsTab, useFinalizedTab,
 * useScriptDetail):
 *  - Module-level api singleton via `createCampaignDetailApi()`.
 *  - staleTime: 30_000 (roster data).
 *  - No manual retry config — the global QueryClient already disables retry
 *    for 4xx (including 404) and allows one retry for 5xx/network.
 *  - Returns the raw useQuery result (same shape as siblings).
 */
import { useQuery } from '@tanstack/react-query';

import type { ResolveCreatorResult } from '../api/campaignQueries';
import { createCampaignDetailApi } from '../api/campaignQueries';
import { campaignKeys } from '../queryKeys';

const api = createCampaignDetailApi();

/**
 * Resolves which tab, pill, and page a creator lands on inside a campaign.
 *
 * @param campaignId  The campaign ObjectID (from useResolvedCampaignId).
 * @param username    The creator's platform username. Passing `undefined` or
 *                    an empty string keeps the query idle — callers don't need
 *                    to guard this separately.
 * @param enabled     External gate. Pass `false` (or leave the default) when
 *                    the creator is already visible in the loaded rows so the
 *                    resolver call is skipped entirely. The caller is
 *                    responsible for gating on "rows are ready AND creator
 *                    not found in current page."
 */
export function useResolveCreatorByUsername(
  campaignId: string,
  username: string | undefined,
  enabled: boolean,
  /** Page size the destination tab will render with. The BE computes
   *  `page = rank/limit + 1`, so this must match or the redirect lands on the
   *  wrong page. Part of the cache key: the same creator resolves to a
   *  DIFFERENT page at a different size. */
  pageSize?: number,
) {
  return useQuery<ResolveCreatorResult>({
    queryKey: [...campaignKeys.resolveCreator(campaignId, username ?? ''), pageSize ?? 'default'],
    queryFn: () => api.resolveCreatorByUsername(campaignId, username!, pageSize),
    enabled: enabled && !!campaignId && !!username,
    staleTime: 30_000,
  });
}
